/**
 * Support Page - FAQ and contact form for investors
 *
 * Route: /support
 *
 * Provides:
 * - FAQ accordion with common investor questions
 * - Support request form (name, email, category, question)
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Navigate, useNavigate } from '@tanstack/react-router'

import { useAuth } from '@/common/hooks/useAuth'
import { useUserProfile } from '@/common/hooks/useUserProfile'
import { useToast } from '@/common/hooks/useToast'
import { Text } from '@/common/components/text'
import { sprinkles } from '@/core/styles/sprinkles.css'
import { Stack } from '@/common/components/stack'
import { Accordion } from '@/common/components/accordion'
import { Form } from '@/common/components/form'
import { Input } from '@/common/components/input'
import { Select, type SelectOption } from '@/common/components/select'
import { Textarea } from '@/common/components/textarea'
import { Button } from '@/common/components/button'
import { submitSupportRequest, type SupportRole } from '@/features/support/api/support'
import { deriveSupportRole } from '@/features/support/deriveSupportRole'

import * as styles from './index.css'

export const Route = createFileRoute({
  component: SupportPage,
})

// =============================================================================
// FAQ Content
// =============================================================================

const INVESTOR_FAQ_ITEMS = [
  {
    id: 'filter-properties',
    question: 'How do I filter the properties that I\'m seeing?',
    answer: 'Go to the "My Buyboxes" tab of the dashboard and click "Create Buybox". From there, you can name this Buybox, select your notification preferences, and filter by value, type, location, conditions, and more.',
  },
  {
    id: 'buybox-limit',
    question: 'How many buy boxes can I create?',
    answer: 'Unlimited. Create as many as you need.',
  },
  {
    id: 'submit-offer',
    question: 'How do I submit an offer?',
    answer: 'Open any property and click "Submit Offer." Fill in your offer details and submit. You can track status from your Offers page. You\'ll get an email notification with the agent in copy when you submit an offer or ask a question.',
  },
  {
    id: 'offer-received',
    question: 'How do I know if my offer was received?',
    answer: 'You\'ll see it in your offers list immediately, and you\'ll receive a confirmation email.',
  },
  {
    id: 'email-notifications',
    question: 'Will I still get email notifications?',
    answer: 'Yes. You\'ll receive emails for new buy box matches and offer status updates. You can adjust your notification preferences anytime in notification settings (click on your name in the top right corner, then select "Notification Settings").',
  },
  {
    id: 'property-photos',
    question: 'Where are the property photos?',
    answer: 'Photos are displayed directly on each property listing.',
  },
  {
    id: 'edit-withdraw-offer',
    question: 'Can I edit or withdraw an offer?',
    answer: 'Yes, you can withdraw an offer before it\'s accepted. You can modify or withdraw an offer from your Offers page.',
  },
  {
    id: 'platform-fee',
    question: 'What is the platform fee?',
    answer: 'The platform fee is a fee charged on each closed transaction. It is 1% of the final sales price if closing with a non-Orchard title company, or 0.5% if closing with Orchard Title or an Orchard affiliate.',
  },
  {
    id: 'platform-fee-payment',
    question: 'When and how is the platform fee paid?',
    answer: 'The platform fee is due at closing and is reflected on the settlement statement as a fee charged to the buyer. If the title company cannot include it on the settlement statement, you are responsible for paying it directly to Orchard Technologies, Inc. via wire transfer or ACH.',
  },
  {
    id: 'orchard-title-discount',
    question: 'Is there a discount for using Orchard Title?',
    answer: 'Yes. Transactions that close using Orchard Title or an Orchard affiliate receive a reduced platform fee of 0.5% instead of 1%, effectively saving you 0.5% on the transaction.',
  },
  {
    id: 'new-agreement',
    question: 'Why do I need to sign a new agreement?',
    answer: 'The updated investor agreement reflects the introduction of the platform fee and other updated terms. All investors must review and sign the updated agreement to continue accessing properties and submitting offers on Orchard Offers.',
  },
  {
    id: 'contact-help',
    question: 'Who do I contact for help?',
    answer: 'You can submit any questions or issues using the form below.',
  },
]

const AGENT_FAQ_ITEMS = [
  {
    id: 'submit-property',
    question: 'How do I submit a property?',
    answer: 'Go to "My Properties" and click "Submit Property". Enter the address (we\'ll auto-complete and normalize it), add property details, upload photos, and set your asking price. When you submit, we automatically pull a valuation and publish the listing to our investor network.',
  },
  {
    id: 'mls-requirement',
    question: 'Does my property have to be on MLS, and are there time limits?',
    answer: 'No — off-MLS properties are welcome. If your property is listed on MLS, we\'ll check how long it\'s been there: MLS listings older than 7 days at submission time aren\'t eligible. Washington is the exception — state rules require homes to be listed on MLS before they can be marketed, so the 7-day limit doesn\'t apply to Washington properties. If we can\'t find the property in MLS data (for example, it\'s outside our integrated markets), we\'ll allow it through. Investors can also filter their feed by MLS status.',
  },
  {
    id: 'property-valuation',
    question: 'How is my property valued?',
    answer: 'When you submit, we automatically pull a market estimate from House Canary using the property address. The estimate is used internally to support investor features like auto-bidding — the platform stays neutral on value, and you set your own asking price.',
  },
  {
    id: 'cant-submit-property',
    question: 'Why can\'t I submit my property?',
    answer: 'The most common reasons are: your training modules aren\'t complete yet, the property has been on MLS for more than 7 days (this limit doesn\'t apply in Washington), required fields or photos are missing, or the address couldn\'t be normalized. If none of those apply, send us a note using the form below.',
  },
  {
    id: 'training-required',
    question: 'Why do I have to complete training before submitting?',
    answer: 'We ask every external agent to finish the training modules once before their first property submission. It\'s a one-time walkthrough of how the marketplace works, and it makes sure your first listing goes smoothly. You can revisit the materials any time from your account menu.',
  },
  {
    id: 'edit-withdraw-listing',
    question: 'Can I edit or withdraw a listing after submitting?',
    answer: 'Yes. Open the property and click "Edit" to update photos, price, or details. You can withdraw a listing any time before an offer is accepted. Once an offer is accepted, contact support to make changes.',
  },
  {
    id: 'offer-notifications',
    question: 'How do I know when I receive an offer?',
    answer: 'You\'ll receive an email as soon as an offer is submitted on your listing. You can see all offers on your "Offers" page. You can adjust email preferences any time in notification settings (click your name in the top right, then "Notification Settings").',
  },
  {
    id: 'respond-to-offer',
    question: 'How do I respond to an offer?',
    answer: 'Open the offer from your "Offers" page and choose Accept, Counter, or Decline. Counters let you adjust price and key terms before sending back. The investor is notified immediately when you respond.',
  },
  {
    id: 'message-buyer',
    question: 'Can I communicate with the investor before accepting?',
    answer: 'Yes. Every offer has a message thread attached — use it to clarify terms, ask questions, or negotiate informally. All messages are logged with the offer so nothing gets lost.',
  },
  {
    id: 'agent-platform-fee',
    question: 'Am I charged a platform fee?',
    answer: 'No. The platform fee is paid by the investor (buyer), not the agent or seller. It\'s 1% of the final sales price, or 0.5% if the buyer closes using Orchard Title or an Orchard affiliate.',
  },
  {
    id: 'commission',
    question: 'How does my commission work?',
    answer: 'Your commission is negotiated between you and your seller per your listing agreement — we don\'t take a cut. Commissions are paid at closing the same way they would on any other sale.',
  },
  {
    id: 'who-sees-listings',
    question: 'Who sees my listings?',
    answer: 'Only our vetted network of cash-offer investors. Listings are not public, don\'t appear on consumer real estate sites, and are visible only to qualified investors on the marketplace.',
  },
  {
    id: 'update-profile',
    question: 'How do I update my brokerage, license, or contact info?',
    answer: 'Send us a message through the form below with the changes, and our support team will update your profile. Self-serve profile editing is coming soon.',
  },
  {
    id: 'agent-contact-help',
    question: 'Who do I contact for help?',
    answer: 'Use the support form below — our team typically responds within 1 business day. For urgent issues on an active offer, include "urgent" in your message so we can prioritize it.',
  },
]

// =============================================================================
// Form Schema
// =============================================================================

const supportFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().min(1, 'Email is required').email('Please enter a valid email'),
  category: z.string().min(1, 'Please select a category'),
  question: z.string().min(1, 'Please enter your question'),
})

type SupportFormData = z.infer<typeof supportFormSchema>

const CATEGORY_OPTIONS: SelectOption[] = [
  { value: 'general', label: 'General question' },
  { value: 'property', label: 'Property question' },
  { value: 'dashboard', label: 'Dashboard question' },
]

// =============================================================================
// Main Component
// =============================================================================

function SupportPage() {
  const { roles, token } = useAuth()
  const { data: userProfile, resetTraining } = useUserProfile()
  const { showErrorToast } = useToast()
  const navigate = useNavigate()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isResettingTraining, setIsResettingTraining] = useState(false)

  const supportRole: SupportRole | null = deriveSupportRole(
    roles,
    userProfile?.profile?.agent_type,
  )

  // MRG-281: only external agents see the replay-training affordance.
  // Internal agents are auto-stamped at provision time and never went
  // through the guided tour to begin with.
  const isExternalAgent = userProfile?.profile?.agent_type === 'external'

  const handleReplayTraining = async () => {
    setIsResettingTraining(true)
    try {
      await resetTraining()
      // Resetting flips `training_completed_at` back to null. The global
      // `AgentTrainingTourProvider` mounted in `_auth.tsx` detects the
      // change and auto-launches the welcome modal once the agent lands
      // on `/properties`.
      navigate({ to: '/properties' })
    } catch (err) {
      console.error('Failed to reset training:', err)
      showErrorToast('Could not start the training. Please try again.')
    } finally {
      setIsResettingTraining(false)
    }
  }

  // Get user's name and email for pre-population
  const profile = userProfile?.profile
  const defaultName = profile?.first_name && profile?.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : profile?.first_name || ''
  const defaultEmail = userProfile?.email || ''

  const form = useForm<SupportFormData>({
    resolver: zodResolver(supportFormSchema),
    defaultValues: {
      name: defaultName,
      email: defaultEmail,
      category: '',
      question: '',
    },
  })

  const { formState: { isSubmitting } } = form

  // Admin-only users have no support experience in this product (they use Slack/Guru).
  if (supportRole === null) {
    return <Navigate to="/properties" replace />
  }

  const faqItems = supportRole === 'agent' ? AGENT_FAQ_ITEMS : INVESTOR_FAQ_ITEMS

  // Resources rendered in the new "Resources" section. Each entry is
  // self-describing so future resources (PDFs, deep links, replay
  // affordances, etc.) can be added by appending to this list without
  // touching the section JSX. Hide-when-empty behavior is handled in the
  // render path below.
  interface SupportResource {
    id: string
    title: string
    description: string
    actionLabel: string
    pendingLabel?: string
    actionDisabled?: boolean
    onAction: () => void
  }
  const resources: SupportResource[] = []
  if (isExternalAgent) {
    resources.push({
      id: 'replay-training',
      title: 'Replay Training',
      description:
        'Walk through the marketplace tour again to revisit how listings, offers, and submissions work.',
      actionLabel: 'Replay Training',
      pendingLabel: 'Starting…',
      actionDisabled: isResettingTraining,
      onAction: handleReplayTraining,
    })
  }

  const handleSubmit = async (data: SupportFormData) => {
    if (!token) return

    try {
      await submitSupportRequest(token, { ...data, role: supportRole })
      setIsSubmitted(true)
    } catch (error) {
      console.error('Failed to submit support request:', error)
      showErrorToast('Failed to submit support request. Please try again.')
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Page Header */}
        <Stack gap="8">
          <Text as="h1" variant="h4" weight="bold">
            Support
          </Text>
          <Text
            variant="body"
            color="muted"
            className={sprinkles({ display: { base: 'block', tabletDown: 'none' } })}
          >
            Find answers to common questions or submit a support request.
          </Text>
        </Stack>

        {/* FAQ Section */}
        <section className={styles.section}>
          <Text as="h2" variant="bodyLg" weight="bold" className={styles.sectionTitle}>
            Frequently Asked Questions
          </Text>

          <Accordion type="multiple" className={styles.accordion}>
            {faqItems.map((item) => (
              <Accordion.Item key={item.id} value={item.id}>
                <Accordion.Trigger className={styles.accordionTrigger}>
                  <Text variant="body" weight="bold">{item.question}</Text>
                </Accordion.Trigger>
                <Accordion.Content className={styles.accordionContent}>
                  <Text variant="body">{item.answer}</Text>
                </Accordion.Content>
              </Accordion.Item>
            ))}
          </Accordion>
        </section>

        {/* Resources Section — currently houses the MRG-281 replay-training
            link (external agents only). Built as a list so additional
            resources can drop in without touching the section shell. The
            section itself is hidden when there are no resources to show. */}
        {resources.length > 0 && (
          <section className={styles.section}>
            <Text as="h2" variant="bodyLg" weight="bold" className={styles.sectionTitle}>
              Resources
            </Text>
            <Text variant="body" color="muted" className={styles.sectionDescription}>
              Helpful resources for using the marketplace.
            </Text>

            <div className={styles.resourceList}>
              {resources.map((resource) => (
                <div key={resource.id} className={styles.resourceItem}>
                  <div className={styles.resourceItemBody}>
                    <Text variant="body" weight="bold">{resource.title}</Text>
                    <Text variant="bodySm" color="muted">{resource.description}</Text>
                  </div>
                  <div className={styles.resourceItemAction}>
                    <Button
                      variant="secondary"
                      onClick={resource.onAction}
                      disabled={resource.actionDisabled}
                    >
                      {resource.actionDisabled && resource.pendingLabel
                        ? resource.pendingLabel
                        : resource.actionLabel}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Contact Form Section */}
        <section className={styles.section}>
          <Text as="h2" variant="bodyLg" weight="bold" className={styles.sectionTitle}>
            Submit a Support Request
          </Text>

          {isSubmitted ? (
            <Text variant="body" className={styles.successMessage}>
              Your support request has been submitted successfully. You'll receive a copy via email.
            </Text>
          ) : (
            <>
              <Text variant="body" color="muted" className={styles.sectionDescription}>
                Have a question that's not covered above? Send us a message and we'll get back to you.
              </Text>

              <Form form={form} onSubmit={handleSubmit} className={styles.form}>
                <Stack gap="16">
                  <div className={styles.formRow}>
                    <Input
                      name="name"
                      control={form.control}
                      label="Name"
                    />
                    <Input
                      name="email"
                      control={form.control}
                      label="Email"
                      type="email"
                    />
                  </div>

                  <Select
                    name="category"
                    control={form.control}
                    label="Category"
                    options={CATEGORY_OPTIONS}
                    placeholder="Select a category"
                  />

                  <Textarea
                    name="question"
                    control={form.control}
                    label="Your question"
                    rows={5}
                    maxLength={10000}
                  />

                  <Form.Error variant="box" />

                  <div className={styles.formActions}>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </Button>
                  </div>
                </Stack>
              </Form>
            </>
          )}
        </section>
      </div>
    </div>
  )
}
